import { useEffect, useState } from 'react';
import { Form, InputNumber, Modal, Typography } from 'antd';
import { InventoryService, ToastMessage } from '@utility/index';
import type { InventorySuggestedTransferItem } from 'schemas/inventorySuggestedTransfers';

const { Text } = Typography;

type QuantityFormValues = {
  suggestedQuantity: number;
};

type SuggestedTransferQuantityModalProps = {
  open: boolean;
  item?: InventorySuggestedTransferItem | null;
  onClose: () => void;
  onUpdated: (item: InventorySuggestedTransferItem) => void;
};

export function SuggestedTransferQuantityModal({
  open,
  item,
  onClose,
  onUpdated,
}: SuggestedTransferQuantityModalProps) {
  const [form] = Form.useForm<QuantityFormValues>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        suggestedQuantity: item?.suggestedQuantity ?? 1,
      });
    } else {
      form.resetFields();
    }
  }, [form, item?.suggestedQuantity, open]);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!item?._id) return;

    try {
      const values = await form.validateFields();
      setLoading(true);

      await InventoryService.updateSuggestedTransfer(item._id, {
        suggestedQuantity: values.suggestedQuantity,
      });

      ToastMessage.success('Suggested quantity updated successfully');
      onUpdated({
        ...item,
        suggestedQuantity: values.suggestedQuantity,
      });
      onClose();
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        return;
      }
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Edit Suggested Quantity"
      open={open}
      centered
      onCancel={handleClose}
      onOk={handleConfirm}
      okText="Save"
      confirmLoading={loading}
      cancelButtonProps={{ disabled: loading }}
      destroyOnHidden
    >
      <div className="mb-3">
        <Text type="secondary">SKU</Text>
        <Text strong className="ml-2">
          {item?.productId?.p_sku || 'N/A'}
        </Text>
      </div>
      <Form
        form={form}
        layout="vertical"
        initialValues={{ suggestedQuantity: item?.suggestedQuantity ?? 1 }}
      >
        <Form.Item
          name="suggestedQuantity"
          label="Suggested Quantity"
          rules={[
            { required: true, message: 'Enter a suggested quantity' },
            {
              type: 'number',
              min: 1,
              message: 'Suggested quantity must be at least 1',
            },
          ]}
        >
          <InputNumber min={1} precision={0} style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
